import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Globe, Palette, Eye, Link2, Copy, ExternalLink, 
  Plus, Trash2, Edit2, Save, Wrench, Settings, CheckCircle,
  PaintBucket, Wind, Sparkles, Layout, Upload, Download, IndianRupee, Car, FileSpreadsheet, Image, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
  { id: 'modern', name: 'Modern Dark', description: 'Dark theme with gradient header', preview: 'bg-slate-900' },
  { id: 'classic', name: 'Classic Light', description: 'Clean white with orange accents', preview: 'bg-white border' },
  { id: 'minimal', name: 'Minimal', description: 'Simple, centered design', preview: 'bg-white' }
];

const SERVICE_CATEGORIES = [
  { id: 'general', name: 'General Service', icon: Wrench },
  { id: 'denting', name: 'Denting & Painting', icon: PaintBucket },
  { id: 'ac', name: 'AC Service', icon: Wind },
  { id: 'vas', name: 'VAS (Polishing, Detailing)', icon: Sparkles },
  { id: 'other', name: 'Other Repairs', icon: Settings }
];

const BookingPageSettings = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [config, setConfig] = useState({
    template: 'modern',
    show_pricing: false,
    use_propitstop_pricing: true,
    custom_tagline: '',
    services: [],
    hero_image_url: null
  });
  const [bookingUrl, setBookingUrl] = useState('');
  const [garageId, setGarageId] = useState('');
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    price: '',
    category: 'general',
    show_price: true
  });
  
  // Pricing state
  const [pricing, setPricing] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('garage_token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchConfig();
    fetchVehiclesAndCategories();
  }, []);

  const fetchConfig = async () => {
    try {
      // Get garage_id from token
      const token = localStorage.getItem('garage_token');
      if (!token) {
        console.error('No garage token found');
        setLoading(false);
        return;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const gId = payload.garage_id;
      setGarageId(gId);
      
      const res = await fetch(`${API_URL}/api/garage-booking/config?garage_id=${gId}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      
      if (data.success) {
        setConfig({
          ...data.config,
          hero_image_url: data.config?.hero_image_url || null
        });
        setBookingUrl(`${window.location.origin}/book/${data.slug}`);
      }
      
      // Fetch pricing
      const pricingRes = await fetch(`${API_URL}/api/garage-booking/pricing/${gId}`, {
        headers: getAuthHeaders()
      });
      const pricingData = await pricingRes.json();
      if (pricingData.success) {
        setPricing(pricingData.pricing || {});
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchVehiclesAndCategories = async () => {
    try {
      const [vehiclesRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/garage-booking/pricing/vehicles-with-categories`),
        fetch(`${API_URL}/api/garage-booking/pricing/categories`)
      ]);
      
      const vehiclesData = await vehiclesRes.json();
      const categoriesData = await categoriesRes.json();
      
      if (vehiclesData.success) setVehicles(vehiclesData.vehicles);
      if (categoriesData.success) setCategories(categoriesData.categories);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/config?garage_id=${garageId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(config)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Booking page settings saved!');
        setBookingUrl(`${window.location.origin}${data.booking_url}`);
      } else {
        toast.error(data.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSavePricing = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/pricing/${garageId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(pricing)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Pricing saved successfully!');
      } else {
        toast.error(data.detail || 'Failed to save pricing');
      }
    } catch (error) {
      toast.error('Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDownloadTemplate = () => {
    window.open(`${API_URL}/api/garage-booking/pricing/${garageId}/download-template`, '_blank');
    toast.success('Downloading pricing template...');
  };
  
  const handleUploadPricing = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/pricing/${garageId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('garage_token')}` },
        body: formData
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        // Refresh pricing data
        const pricingRes = await fetch(`${API_URL}/api/garage-booking/pricing/${garageId}`, {
          headers: getAuthHeaders()
        });
        const pricingData = await pricingRes.json();
        if (pricingData.success) {
          setPricing(pricingData.pricing || {});
        }
      } else {
        toast.error(data.detail || 'Failed to upload pricing');
      }
    } catch (error) {
      toast.error('Failed to upload pricing file');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };
  
  const handleUploadHeroImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPG, PNG, and WebP are allowed.');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB allowed.');
      return;
    }
    
    setUploadingHero(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/hero-image/${garageId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('garage_token')}` },
        body: formData
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Hero image uploaded successfully!');
        setConfig({ ...config, hero_image_url: data.hero_image_url });
      } else {
        toast.error(data.detail || 'Failed to upload hero image');
      }
    } catch (error) {
      toast.error('Failed to upload hero image');
    } finally {
      setUploadingHero(false);
      event.target.value = '';
    }
  };
  
  const handleDeleteHeroImage = async () => {
    try {
      const res = await fetch(`${API_URL}/api/garage-booking/hero-image/${garageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Hero image removed');
        setConfig({ ...config, hero_image_url: null });
      } else {
        toast.error(data.detail || 'Failed to delete hero image');
      }
    } catch (error) {
      toast.error('Failed to delete hero image');
    }
  };
  
  const updateServicePrice = (serviceId, brand, category, price) => {
    const key = `${brand}_${category}`;
    setPricing(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [key]: parseFloat(price) || 0
      }
    }));
  };
  
  const getServicePrice = (serviceId, brand, category) => {
    const key = `${brand}_${category}`;
    return pricing[serviceId]?.[key] || '';
  };
  
  // Get unique brands from vehicles
  const uniqueBrands = [...new Set(vehicles.map(v => v.brand))].sort();

  const handleAddService = () => {
    if (!serviceForm.name) {
      toast.error('Service name is required');
      return;
    }
    
    const newService = {
      id: editingService?.id || Date.now().toString(),
      name: serviceForm.name,
      description: serviceForm.description,
      price: serviceForm.price ? parseFloat(serviceForm.price) : null,
      category: serviceForm.category,
      show_price: serviceForm.show_price,
      is_active: true
    };
    
    if (editingService) {
      setConfig({
        ...config,
        services: config.services.map(s => s.id === editingService.id ? newService : s)
      });
    } else {
      setConfig({
        ...config,
        services: [...config.services, newService]
      });
    }
    
    setShowServiceDialog(false);
    setEditingService(null);
    setServiceForm({ name: '', description: '', price: '', category: 'general', show_price: true });
    toast.success(editingService ? 'Service updated' : 'Service added');
  };

  const handleDeleteService = (serviceId) => {
    setConfig({
      ...config,
      services: config.services.filter(s => s.id !== serviceId)
    });
    toast.success('Service removed');
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      description: service.description || '',
      price: service.price?.toString() || '',
      category: service.category || 'general',
      show_price: service.show_price !== false
    });
    setShowServiceDialog(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast.success('Booking URL copied!');
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.pageBg} p-4 md:p-6`}>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className={`text-2xl md:text-3xl font-bold ${theme.text}`}>
              Booking Page Settings
            </h1>
            <p className={theme.textMuted}>
              Customize your online booking page for customers
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => {
                if (bookingUrl) {
                  window.open(bookingUrl, '_blank');
                } else {
                  toast.error('Booking URL not available yet');
                }
              }} 
              variant="outline" 
              className={theme.border}
              disabled={!bookingUrl}
            >
              <Eye className="w-4 h-4 mr-2" /> Preview
            </Button>
            <Button onClick={activeTab === 'pricing' ? handleSavePricing : handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          </div>
        </div>

        {/* Booking URL */}
        <Card className={`${theme.cardBgSolid} ${theme.border}`}>
          <CardHeader>
            <CardTitle className={`${theme.text} flex items-center gap-2`}>
              <Link2 className="w-5 h-5 text-orange-500" />
              Your Booking Page URL
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={bookingUrl}
                readOnly
                className={`${theme.inputBg} ${theme.border} flex-1`}
              />
              <Button variant="outline" onClick={copyToClipboard} className={theme.border}>
                <Copy className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => window.open(bookingUrl, '_blank')} className={theme.border}>
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
            <p className={`text-sm ${theme.textMuted} mt-2`}>
              Share this link on Google Business, social media, or your website
            </p>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full grid-cols-2 ${theme.cardBgSolid}`}>
            <TabsTrigger value="settings" className="data-[state=active]:bg-orange-600">
              <Settings className="w-4 h-4 mr-2" /> Page Settings
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-orange-600">
              <IndianRupee className="w-4 h-4 mr-2" /> Service Pricing
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6 mt-6">
            {/* Template Selection */}
            <Card className={`${theme.cardBgSolid} ${theme.border}`}>
              <CardHeader>
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <Layout className="w-5 h-5 text-orange-500" />
              Choose Template
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setConfig({ ...config, template: template.id })}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    config.template === template.id
                      ? 'border-orange-500 ring-2 ring-orange-500/20'
                      : `${theme.border} hover:border-orange-500/50`
                  }`}
                >
                  <div className={`h-24 rounded-lg mb-3 ${template.preview}`}></div>
                  <p className={`font-semibold ${theme.text}`}>{template.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>{template.description}</p>
                  {config.template === template.id && (
                    <Badge className="mt-2 bg-orange-500">Selected</Badge>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hero Image */}
        <Card className={`${theme.cardBgSolid} ${theme.border}`}>
          <CardHeader>
            <CardTitle className={`${theme.text} flex items-center gap-2`}>
              <Image className="w-5 h-5 text-orange-500" />
              Hero Banner Image
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-sm ${theme.textMuted} mb-4`}>
              Add a full-width banner image at the top of your booking page. Recommended size: 1920x400 pixels.
            </p>
            
            {config.hero_image_url ? (
              <div className="relative">
                <img 
                  src={`${API_URL}/api${config.hero_image_url}`}
                  alt="Hero Banner"
                  className="w-full h-32 md:h-48 object-cover rounded-lg border border-slate-600"
                />
                <button
                  onClick={handleDeleteHeroImage}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 rounded-full text-white shadow-lg transition-colors"
                  title="Remove hero image"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleUploadHeroImage}
                    className="hidden"
                    id="hero-image-replace"
                  />
                  <label 
                    htmlFor="hero-image-replace"
                    className={`inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium border ${theme.border} cursor-pointer hover:bg-slate-700 transition-colors ${uploadingHero ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingHero ? 'Uploading...' : 'Replace Image'}
                  </label>
                </div>
              </div>
            ) : (
              <div className={`border-2 border-dashed ${theme.border} rounded-lg p-8 text-center`}>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-700/50 flex items-center justify-center">
                  <Image className="w-8 h-8 text-slate-500" />
                </div>
                <p className={`${theme.textMuted} mb-4`}>No hero image uploaded</p>
                <p className={`text-xs ${theme.textMuted} mb-4`}>
                  A default gradient will be shown if no image is uploaded
                </p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleUploadHeroImage}
                  className="hidden"
                  id="hero-image-upload"
                  data-testid="hero-image-input"
                />
                <label 
                  htmlFor="hero-image-upload"
                  data-testid="hero-image-upload-btn"
                  className={`inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-semibold bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white cursor-pointer transition-all shadow-lg hover:shadow-xl ${uploadingHero ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                >
                  <Upload className="w-5 h-5 mr-2" />
                  {uploadingHero ? 'Uploading...' : 'Upload Hero Image'}
                </label>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Services */}
        <Card className={`${theme.cardBgSolid} ${theme.border}`}>
          <CardHeader>
            <CardTitle className={`${theme.text} flex items-center justify-between`}>
              <span className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-500" />
                Services Offered
              </span>
              <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
                <DialogTrigger asChild>
                  <Button 
                    size="sm" 
                    className="bg-orange-500 hover:bg-orange-600"
                    onClick={() => {
                      setEditingService(null);
                      setServiceForm({ name: '', description: '', price: '', category: 'general', show_price: true });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Service
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${theme.cardBgSolid} ${theme.border}`}>
                  <DialogHeader>
                    <DialogTitle className={theme.text}>
                      {editingService ? 'Edit Service' : 'Add Service'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className={theme.textSecondary}>Service Name *</Label>
                      <Input
                        value={serviceForm.name}
                        onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                        placeholder="e.g., General Service"
                        className={`${theme.inputBg} ${theme.border}`}
                      />
                    </div>
                    <div>
                      <Label className={theme.textSecondary}>Category</Label>
                      <Select
                        value={serviceForm.category}
                        onValueChange={(v) => setServiceForm({ ...serviceForm, category: v })}
                      >
                        <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                          {SERVICE_CATEGORIES.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={theme.textSecondary}>Description</Label>
                      <Textarea
                        value={serviceForm.description}
                        onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                        placeholder="Brief description of the service"
                        className={`${theme.inputBg} ${theme.border}`}
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={theme.textSecondary}>Price (optional)</Label>
                        <Input
                          type="number"
                          value={serviceForm.price}
                          onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                          placeholder="e.g., 2999"
                          className={`${theme.inputBg} ${theme.border}`}
                        />
                      </div>
                      <div className="flex items-end">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={serviceForm.show_price}
                            onCheckedChange={(checked) => setServiceForm({ ...serviceForm, show_price: checked })}
                          />
                          <span className={`text-sm ${theme.textSecondary}`}>Show price</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setShowServiceDialog(false)}
                        className={`flex-1 ${theme.border}`}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddService}
                        className="flex-1 bg-orange-500 hover:bg-orange-600"
                      >
                        {editingService ? 'Update' : 'Add'} Service
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {config.services.length === 0 ? (
              <div className={`text-center py-8 ${theme.textMuted}`}>
                <Wrench className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No services added yet</p>
                <p className="text-sm">Add services that customers can book</p>
              </div>
            ) : (
              <div className="space-y-2">
                {config.services.map((service) => {
                  const CategoryIcon = SERVICE_CATEGORIES.find(c => c.id === service.category)?.icon || Wrench;
                  return (
                    <div
                      key={service.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${theme.hoverBg} border ${theme.border}`}
                    >
                      <div className="flex items-center gap-3">
                        <CategoryIcon className="w-5 h-5 text-orange-500" />
                        <div>
                          <p className={`font-medium ${theme.text}`}>{service.name}</p>
                          {service.description && (
                            <p className={`text-sm ${theme.textMuted}`}>{service.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {service.price && service.show_price && (
                          <Badge variant="outline" className="text-green-500 border-green-500/50">
                            ₹{service.price}
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditService(service)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteService(service.id)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing Settings */}
        <Card className={`${theme.cardBgSolid} ${theme.border}`}>
          <CardHeader>
            <CardTitle className={`${theme.text} flex items-center gap-2`}>
              <Settings className="w-5 h-5 text-orange-500" />
              Pricing Display
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${theme.text}`}>Show Prices on Booking Page</p>
                <p className={`text-sm ${theme.textMuted}`}>Display service prices to customers</p>
              </div>
              <Switch
                checked={config.show_pricing}
                onCheckedChange={(checked) => setConfig({ ...config, show_pricing: checked })}
              />
            </div>
            
            {config.show_pricing && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                <div>
                  <p className={`font-medium ${theme.text}`}>Use Propitstop Pricing</p>
                  <p className={`text-sm ${theme.textMuted}`}>Use standard Propitstop service rates</p>
                </div>
                <Switch
                  checked={config.use_propitstop_pricing}
                  onCheckedChange={(checked) => setConfig({ ...config, use_propitstop_pricing: checked })}
                />
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-6 mt-6">
            {/* Bulk Upload/Download */}
            <Card className={`${theme.cardBgSolid} ${theme.border}`}>
              <CardHeader>
                <CardTitle className={`${theme.text} flex items-center justify-between`}>
                  <span className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-orange-500" />
                    Bulk Pricing Management
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className={`flex-1 p-4 rounded-lg ${theme.hoverBg} border ${theme.border}`}>
                    <h3 className={`font-medium ${theme.text} mb-2`}>Download Template</h3>
                    <p className={`text-sm ${theme.textMuted} mb-4`}>
                      Get an Excel template with all vehicles listed. Fill in your prices and upload.
                    </p>
                    <Button onClick={handleDownloadTemplate} variant="outline" className={theme.border}>
                      <Download className="w-4 h-4 mr-2" /> Download Template
                    </Button>
                  </div>
                  
                  <div className={`flex-1 p-4 rounded-lg ${theme.hoverBg} border ${theme.border}`}>
                    <h3 className={`font-medium ${theme.text} mb-2`}>Upload Pricing</h3>
                    <p className={`text-sm ${theme.textMuted} mb-4`}>
                      Upload your completed pricing template to update all prices at once.
                    </p>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleUploadPricing}
                      className="hidden"
                      id="pricing-upload"
                    />
                    <label htmlFor="pricing-upload">
                      <Button 
                        asChild 
                        variant="outline" 
                        className={`${theme.border} cursor-pointer`}
                        disabled={uploading}
                      >
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploading ? 'Uploading...' : 'Upload Excel'}
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Manual Pricing Editor */}
            <Card className={`${theme.cardBgSolid} ${theme.border}`}>
              <CardHeader>
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <IndianRupee className="w-5 h-5 text-orange-500" />
                  Set Pricing by Service & Vehicle Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {config.services.length === 0 ? (
                  <div className={`text-center py-8 ${theme.textMuted}`}>
                    <Wrench className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Add services first in the "Page Settings" tab</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label className={theme.textSecondary}>Select Service</Label>
                      <Select
                        value={selectedService?.id || ''}
                        onValueChange={(v) => setSelectedService(config.services.find(s => s.id === v))}
                      >
                        <SelectTrigger className={`${theme.inputBg} ${theme.border}`}>
                          <SelectValue placeholder="Choose a service to set pricing" />
                        </SelectTrigger>
                        <SelectContent className={`${theme.cardBgSolid} ${theme.border}`}>
                          {config.services.map(service => (
                            <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {selectedService && (
                      <div className="mt-4">
                        <div className={`p-3 rounded-lg ${theme.hoverBg} mb-4`}>
                          <p className={`font-medium ${theme.text}`}>
                            Pricing for: <span className="text-orange-500">{selectedService.name}</span>
                          </p>
                          <p className={`text-sm ${theme.textMuted}`}>
                            Set prices by brand and vehicle category. All models of the same brand+category will share the same price.
                          </p>
                        </div>
                        
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={`${theme.hoverBg}`}>
                                <th className={`p-3 text-left ${theme.text}`}>Brand</th>
                                {categories.map(cat => (
                                  <th key={cat} className={`p-3 text-center ${theme.text}`}>{cat}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {uniqueBrands.map(brand => (
                                <tr key={brand} className={`border-t ${theme.border}`}>
                                  <td className={`p-3 ${theme.text} font-medium`}>{brand}</td>
                                  {categories.map(cat => (
                                    <td key={cat} className="p-2">
                                      <Input
                                        type="number"
                                        value={getServicePrice(selectedService.id, brand, cat)}
                                        onChange={(e) => updateServicePrice(selectedService.id, brand, cat, e.target.value)}
                                        placeholder="0"
                                        className={`${theme.inputBg} ${theme.border} w-24 text-center`}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        <div className="mt-4 flex justify-end">
                          <Button onClick={handleSavePricing} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                            <Save className="w-4 h-4 mr-2" />
                            Save Pricing
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Pricing Summary */}
            {Object.keys(pricing).length > 0 && (
              <Card className={`${theme.cardBgSolid} ${theme.border}`}>
                <CardHeader>
                  <CardTitle className={`${theme.text} flex items-center gap-2`}>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Pricing Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(pricing).map(([serviceId, prices]) => {
                      const service = config.services.find(s => s.id === serviceId);
                      const priceCount = Object.keys(prices).length;
                      return (
                        <div key={serviceId} className={`p-3 rounded-lg ${theme.hoverBg} border ${theme.border}`}>
                          <p className={`font-medium ${theme.text}`}>{service?.name || serviceId}</p>
                          <p className={`text-sm ${theme.textMuted}`}>{priceCount} price entries</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BookingPageSettings;
